import React from 'react';
import { CompareSlot, CompareShowSections } from '@/types/compare.types';
import { LineDiff } from '@hooks/useDiff';
import { Music, Clock, FileText, Tags, Sparkles, AlertCircle } from 'lucide-react';
import { Button, IconButton } from '../ui/Button';
import { Badge } from '../ui/Badge';

interface ComparePanelProps {
  slot: CompareSlot | null;
  slotLabel: 'A' | 'B';
  onChangeSlot?: () => void;
  showSections: CompareShowSections;
  highlightChanges: boolean;
  diffData?: LineDiff[];
  scrollRef?: React.RefObject<HTMLDivElement | null>;
  onScroll?: (scrollTop: number) => void;
}

export const ComparePanel: React.FC<ComparePanelProps> = ({
  slot, slotLabel, onChangeSlot, showSections, diffData, scrollRef, onScroll
}) => {
  if (!slot) return (
    <div className="h-full flex flex-col items-center justify-center p-12 text-center bg-slate-950/20 border-2 border-dashed border-slate-800 m-4 rounded-2xl">
      <Music size={40} className="text-slate-800 mb-4" />
      <h3 className="text-sm font-black text-slate-50 uppercase tracking-widest">Version {slotLabel} Empty</h3>
      <Button
        variant="secondary"
        size="sm"
        onClick={(e) => {
          e.preventDefault();
          onChangeSlot?.();
        }}
        className="mt-4 text-[11px] uppercase"
      >
        Select Song
      </Button>
    </div>
  );

  return (
    <div className="flex flex-col h-full bg-slate-900/50 border-x border-slate-800 overflow-hidden">
      <header className="p-4 bg-slate-900 border-b border-slate-800 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Badge
            variant={slotLabel === 'A' ? 'info-muted' : 'warning-muted'}
            size="md"
            className="w-8 h-8 justify-center font-black"
          >
            {slotLabel}
          </Badge>
          <div>
            <h4 className="text-sm font-black text-slate-100 truncate max-w-[200px]">{slot.title}</h4>
            <div className="flex items-center gap-2 text-[11px] text-slate-400 font-bold uppercase tracking-tighter">
              <Clock size={10} /> {new Date(slot.timestamp).toLocaleTimeString()}
            </div>
          </div>
        </div>
        <IconButton
          variant="ghost"
          size="xs"
          onClick={onChangeSlot}
          title="Change slot"
        >
          <AlertCircle size={14} />
        </IconButton>
      </header>

      <div
        ref={scrollRef}
        onScroll={(e) => onScroll?.((e.target as HTMLDivElement).scrollTop)}
        className="flex-grow overflow-y-auto p-6 space-y-8 custom-scrollbar"
      >
        {showSections.metadata && (
          <section className="space-y-2">
            <h5 className="text-[11px] font-black text-accent uppercase tracking-widest flex items-center gap-2"><Sparkles size={12}/> Metadata</h5>
            <div className="bg-slate-950 p-3 rounded-lg border border-slate-800 text-[11px] text-slate-400">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-slate-400 block mb-0.5">Title</span> {slot.title}</div>
                <div><span className="text-slate-400 block mb-0.5">Source</span> {slot.source}</div>
              </div>
            </div>
          </section>
        )}

        {showSections.lyrics && (
          <section className="space-y-2">
            <h5 className="text-[11px] font-black text-accent uppercase tracking-widest flex items-center gap-2"><Music size={12}/> Lyrical Blueprint</h5>
            <div className="bg-slate-950 p-4 rounded-lg border border-slate-800 text-[12px] font-mono leading-relaxed text-slate-300 whitespace-pre-wrap">
              {diffData ? diffData.map((l, i) => (
                <div key={i} className={`line-${l.type} ${l.isStructural ? 'font-bold text-accent/80' : ''}`}>
                  {slotLabel === 'A' ? l.lineA : l.lineB}
                </div>
              )) : slot.lyrics}
            </div>
          </section>
        )}

        {showSections.production && slot.productionNotes && (
          <section className="space-y-2">
            <h5 className="text-[11px] font-black text-accent uppercase tracking-widest flex items-center gap-2"><FileText size={12}/> Production Notes</h5>
            <p className="bg-slate-950 p-3 rounded-lg border border-slate-800 text-[11px] text-slate-400 leading-relaxed italic">{slot.productionNotes}</p>
          </section>
        )}

        {showSections.sunoTags && slot.sunoStyleTags && (
          <section className="space-y-2">
            <h5 className="text-[11px] font-black text-accent uppercase tracking-widest flex items-center gap-2"><Tags size={12}/> Machine Tags</h5>
            <div className="flex flex-wrap gap-2">
              {slot.sunoStyleTags.map((tag, i) => (
                <Badge key={i} variant="default" size="xs">{tag}</Badge>
              ))}
            </div>
          </section>
        )}
      </div>
      <style>{`
        .line-added { background: rgba(100, 255, 100, 0.1); color: #88ff88; }
        .line-removed { background: rgba(255, 100, 100, 0.1); color: #ff8888; text-decoration: line-through; }
        .line-modified { background: rgba(245, 158, 11, 0.1); }
      `}</style>
    </div>
  );
};

export default ComparePanel;
